import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { ElaborazioneResponse } from '@/types/api-responses.ts';

const POLLING_INTERVAL = 2000;

const NOMI_MESE = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

interface CasseConfigEntry {
  codice: string;
  nome: string;
  descrizione: string | null;
  estensione_archivio: string;
  archivio_multiplo: boolean;
}

interface TipoCaricamentoEntry {
  codice: string;
  label: string;
}

interface UploadOutcome {
  ok: boolean;
  filename: string;
}

interface CassaUploadChangedDetail {
  archivio: UploadOutcome;
  accessori: Record<string, UploadOutcome>;
}

export class Elaborazioni extends AppComponent {
  static override componentName = 'wc-elaborazioni';

  protected override get templateId() { return 'elaborazioni'; }

  private pollingTimers = new Map<number, ReturnType<typeof setInterval>>();
  private elaborazioni: ElaborazioneResponse[] = [];
  private detailCache = new Map<number, ElaborazioneResponse>();
  private feniceEnabled = false;
  private feniceStaging = false;
  private casseConfig = new Map<string, CasseConfigEntry>();

  /** Cache tipi per cassa: chiave = codice cassa, valore = array tipi.
   *  Array vuoto = mono-tipo (lo step di selezione tipo viene saltato). */
  private tipiPerCassa = new Map<string, TipoCaricamentoEntry[]>();

  private currentStep = 1;
  private uploadState: CassaUploadChangedDetail = { archivio: { ok: false, filename: '' }, accessori: {} };

  protected override afterRender(): void {
    this.feniceEnabled = this.hasAttribute('data-fenice-enabled');
    this.feniceStaging = this.hasAttribute('data-fenice-staging');
    void this.caricaCasseConfig();
    this.initDefaults();
    this.setupActions();
    this.caricaStorico();
  }

  private async caricaCasseConfig(): Promise<void> {
    try {
      const r = await fetch('/api/casse/config', { credentials: 'same-origin' });
      if (!r.ok) return;
      const list = (await r.json()) as CasseConfigEntry[];
      this.casseConfig = new Map(list.map(c => [c.codice, c]));
      this.populateCassaSelect();
    } catch {
      // Lascia mappa vuota: la select resta vuota, lo step 1 mostra "Avanti" disabled per anno/mese.
    }
  }

  private populateCassaSelect(): void {
    const select = this.$('[data-cassa-select]') as HTMLSelectElement | null;
    if (!select) return;
    const current = select.value;
    select.innerHTML = '';
    for (const cfg of this.casseConfig.values()) {
      const opt = document.createElement('option');
      opt.value = cfg.codice;
      opt.textContent = this.cassaLabel(cfg.codice);
      select.appendChild(opt);
    }
    if (current && this.casseConfig.has(current)) {
      select.value = current;
    }
    if (select.value) void this.onCassaChanged(select.value);
  }

  // --- Tipi caricamento (multi-tipo) ---

  /** Fetch + render UI tipo per la cassa scelta. Cache `tipiPerCassa`. */
  private async onCassaChanged(cassa: string): Promise<void> {
    if (!cassa) { this.renderTipoSelect([]); return; }
    let tipi = this.tipiPerCassa.get(cassa);
    if (!tipi) {
      const r = await this.api.elaborazioni.tipi(cassa as Parameters<typeof this.api.elaborazioni.tipi>[0]);
      tipi = r.ok ? r.data : [];
      this.tipiPerCassa.set(cassa, tipi);
    }
    this.renderTipoSelect(tipi);
  }

  private renderTipoSelect(tipi: TipoCaricamentoEntry[]): void {
    const wrap = this.$('[data-tipo-wrap]') as HTMLElement | null;
    const sel = this.$('[data-tipo-select]') as HTMLSelectElement | null;
    if (!wrap || !sel) return;
    sel.innerHTML = '';
    if (tipi.length === 0) {
      wrap.style.display = 'none';
      return;
    }
    for (const t of tipi) {
      const opt = document.createElement('option');
      opt.value = t.codice;
      opt.textContent = t.label;
      sel.appendChild(opt);
    }
    wrap.style.display = '';
  }

  private currentTipi(): TipoCaricamentoEntry[] {
    const cassa = this.field('cassa');
    return this.tipiPerCassa.get(cassa) ?? [];
  }

  private cassaLabel(codice: string): string {
    const cfg = this.casseConfig.get(codice);
    return cfg ? `${codice} - ${cfg.nome}` : codice;
  }

  private field(name: string): string {
    return (this.$(`[data-field="${name}"]`) as HTMLInputElement | null)?.value ?? '';
  }

  private initDefaults(): void {
    const now = new Date();
    const anno = this.$('[data-field="anno"]') as HTMLInputElement | null;
    const mese = this.$('[data-field="mese"]') as HTMLInputElement | null;
    if (anno) anno.value = String(now.getFullYear());
    if (mese) mese.value = String(now.getMonth() + 1);
  }

  private setupActions(): void {
    this.delegate.on('change', '[data-cassa-select]', (e: Event) => {
      const select = e.target as HTMLSelectElement;
      void this.onCassaChanged(select.value);
    });
    this.delegate.on('click', '[data-action="step-avanti"]', (e: Event) => {
      e.preventDefault();
      this.goToStep(this.currentStep + 1);
    });
    this.delegate.on('click', '[data-action="step-indietro"]', (e: Event) => {
      e.preventDefault();
      this.goToStep(this.currentStep - 1);
    });
    this.delegate.on('click', '[data-action="avvia"]', (e: Event) => {
      e.preventDefault();
      this.handleAvvia();
    });
    this.delegate.on('click', '[data-action="elimina"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleElimina(id);
    });
    this.delegate.on('click', '[data-action="upload-fenice"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleUploadFenice(id);
    });
    this.delegate.on('click', '[data-action="download-csv"]', (e: Event) => {
      e.preventDefault();
      const id = this.idFrom(e);
      if (id !== null) this.handleDownloadCsv(id);
    });
    this.delegate.on('click', '[data-action="allinea-storico"]', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      const el = (e.target as HTMLElement).closest('[data-action="allinea-storico"]') as HTMLElement | null;
      const cassa = el?.dataset.cassa ?? '';
      const anno = Number(el?.dataset.anno ?? '0');
      const mese = Number(el?.dataset.mese ?? '0');
      if (cassa && anno && mese) void this.handleAllinea(cassa, anno, mese);
    });
    this.delegate.on('click', '[data-elab-id]', (e: Event) => {
      if ((e.target as HTMLElement).closest('[data-action]')) return;
      const row = (e.target as HTMLElement).closest('[data-elab-id]') as HTMLElement | null;
      if (row?.dataset.elabId) this.toggleDettaglio(Number(row.dataset.elabId));
    });
    this.delegate.on('cassa-upload-changed', '[data-cassa-upload-host]', (e: Event) => {
      this.uploadState = (e as CustomEvent<CassaUploadChangedDetail>).detail;
      this.updateStepAvantiBtn(2, this.uploadState.archivio.ok);
    });
  }

  // --- Wizard step ---

  private goToStep(step: number): void {
    if (step < 1 || step > 3) return;

    if (step === 2 && this.currentStep === 1) {
      const anno = this.field('anno');
      const mese = this.field('mese');
      const cassa = this.field('cassa');
      if (!cassa) {
        Weext.toast('Seleziona una cassa', { type: 'warning' });
        return;
      }
      if (!anno || !mese) {
        Weext.toast('Seleziona anno e mese', { type: 'warning' });
        return;
      }
      // Validazione tipo per casse multi-tipo (Cremona, Mantova...): se la
      // cassa espone tipi, l'utente deve sceglierne uno; il valore va al
      // POST /elaborazioni come `tipo` e al cassa-upload per la validazione
      // colonne_attese del file.
      if (this.currentTipi().length > 0 && !this.field('tipo')) {
        Weext.toast('Seleziona un tipo di caricamento', { type: 'warning' });
        return;
      }
      this.api.elaborazioni.cleanDir(cassa);
      this.mountCassaUpload(cassa, anno, mese, this.field('tipo'));
    }

    if (step === 3) {
      this.renderRiepilogo();
    }

    this.showStep(step);
    this.currentStep = step;
  }

  private mountCassaUpload(cassa: string, anno: string, mese: string, tipo: string): void {
    const host = this.$('[data-cassa-upload-host]');
    if (!host) return;
    host.innerHTML = '';
    const wc = document.createElement('wc-cassa-upload');
    wc.setAttribute('codice', cassa);
    wc.setAttribute('anno', anno);
    wc.setAttribute('mese', mese);
    if (tipo) wc.setAttribute('tipo', tipo);
    host.appendChild(wc);

    const cassaLabel = this.$('[data-cassa-label]');
    if (cassaLabel) cassaLabel.textContent = this.cassaLabel(cassa);
    const periodoLabel = this.$('[data-periodo-label]');
    if (periodoLabel) {
      const nomeMese = NOMI_MESE[Number(mese) - 1] ?? mese;
      periodoLabel.textContent = `${nomeMese} ${anno}`;
    }

    this.uploadState = { archivio: { ok: false, filename: '' }, accessori: {} };
    this.updateStepAvantiBtn(2, false);
  }

  private updateStepAvantiBtn(step: number, enabled: boolean): void {
    const stepEl = this.$(`[data-step="${step}"]`);
    if (!stepEl) return;
    const btn = stepEl.querySelector('[data-action="step-avanti"]') as HTMLButtonElement | null;
    if (btn) btn.disabled = !enabled;
  }

  private showStep(step: number): void {
    for (let i = 1; i <= 3; i++) {
      const el = this.$(`[data-step="${i}"]`) as HTMLElement | null;
      if (el) el.style.display = i === step ? '' : 'none';
    }
  }

  private renderRiepilogo(): void {
    const el = this.$('[data-riepilogo]');
    if (!el) return;
    const cassa = this.field('cassa');
    const mese = Number(this.field('mese'));
    const anno = this.field('anno');
    const nomeMese = NOMI_MESE[mese - 1] ?? String(mese);
    const accessori = Object.entries(this.uploadState.accessori).map(([key, value]) => ({
      label: key,
      filename: value.filename,
      ok: value.ok,
    }));
    templateParseToElement('elaborazioni-riepilogo', el, {
      cassaLabel: this.cassaLabel(cassa),
      nomeMese,
      anno,
      nomeFileArchivio: this.uploadState.archivio.filename,
      accessori,
    });
  }

  private idFrom(e: Event): number | null {
    const el = (e.target as HTMLElement).closest('[data-id]') as HTMLElement | null;
    return el?.dataset.id ? Number(el.dataset.id) : null;
  }

  private resetWizard(): void {
    this.uploadState = { archivio: { ok: false, filename: '' }, accessori: {} };
    this.currentStep = 1;
    this.showStep(1);
    const host = this.$('[data-cassa-upload-host]');
    if (host) host.innerHTML = '';
  }

  // --- Storico ---

  private async caricaStorico(): Promise<void> {
    const result = await this.api.elaborazioni.lista();
    if (!result.ok) {
      const c = this.$('[data-storico]');
      if (c) templateParseToElement('shared-error-state', c, { message: result.error });
      return;
    }
    this.elaborazioni = result.data.elaborazioni;
    this.renderStorico();
    for (const e of this.elaborazioni) {
      if (e.stato === 'IN_CORSO' || e.stato === 'COMPLETATO') this.startPolling(e.id);
    }
  }

  private renderStorico(): void {
    const container = this.$('[data-storico]');
    if (!container) return;
    const data = this.elaborazioni.map(e => ({
      ...e,
      periodo: `${String(e.mese).padStart(2, '0')}/${e.anno}`,
    }));
    const showTipo = data.some(e => e.tipo);
    templateParseToElement('elaborazioni-storico', container, { elaborazioni: data, feniceEnabled: this.feniceEnabled, showTipo });
  }

  // --- Avvia ---

  private async handleAvvia(): Promise<void> {
    const cassa = this.field('cassa');
    const anno = this.field('anno');
    const mese = this.field('mese');
    if (!cassa) { Weext.toast('Cassa non selezionata', { type: 'warning' }); return; }
    if (!anno || !mese) { Weext.toast('Seleziona anno e mese', { type: 'warning' }); return; }

    const tipo = this.field('tipo');
    const hasTipi = this.currentTipi().length > 0;
    if (hasTipi && !tipo) { Weext.toast('Tipo non selezionato', { type: 'warning' }); return; }

    const result = await this.api.elaborazioni.crea({
      cassa,
      anno: Number(anno),
      mese: Number(mese),
      ...(hasTipi ? { tipo } : {}),
    });
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }

    const { id, cassa: c, anno: a, mese: m } = result.data;
    const now = new Date();
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const nuova: ElaborazioneResponse = {
      id, codice_fenice: 0, codice_cassa: c, anno: a, mese: m, stato: 'IN_CORSO',
      nome_tabella: '', errore: '', file_path: '', importato_at: '',
      created_at: ts, stats: null, warnings: null, importResult: null,
    };
    this.elaborazioni.unshift(nuova);
    this.renderStorico();
    this.startPolling(id);
    this.resetWizard();
  }

  // --- Polling ---

  private startPolling(id: number): void {
    if (this.pollingTimers.has(id)) return;
    this.pollingTimers.set(id, setInterval(() => this.pollElaborazione(id), POLLING_INTERVAL));
  }

  private stopPolling(id: number): void {
    const t = this.pollingTimers.get(id);
    if (t) { clearInterval(t); this.pollingTimers.delete(id); }
  }

  private async pollElaborazione(id: number): Promise<void> {
    const result = await this.api.elaborazioni.dettaglio(id);
    if (!result.ok) return;
    const updated = result.data;
    const idx = this.elaborazioni.findIndex(e => e.id === id);
    if (idx !== -1) this.elaborazioni[idx] = updated;
    const terminale = updated.stato !== 'IN_CORSO' && updated.stato !== 'COMPLETATO';
    if (terminale) { this.stopPolling(id); this.detailCache.set(id, updated); }
    this.renderStorico();
  }

  // --- Elimina ---

  private async handleElimina(id: number): Promise<void> {
    const ok = await Weext.confirm('Eliminare l\'elaborazione? La tabella staging verrà rimossa.', {
      title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger',
    });
    if (!ok) return;
    const result = await this.api.elaborazioni.elimina(id);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }
    Weext.toast('Elaborazione eliminata', { type: 'success' });
    this.elaborazioni = this.elaborazioni.filter(e => e.id !== id);
    this.detailCache.delete(id);
    this.renderStorico();
  }

  // --- Download CSV ---

  private handleDownloadCsv(id: number): void {
    window.open(`/api/elaborazioni/${id}/csv`, '_blank');
  }

  // --- Allineamento da storico ---

  private async handleAllinea(cassa: string, anno: number, mese: number): Promise<void> {
    const ok = await Weext.confirm(
      `Eseguire l'allineamento archivi per il periodo ${String(mese).padStart(2, '0')}/${anno}?`,
      { title: 'Conferma allineamento', confirmText: 'Allinea' },
    );
    if (!ok) return;
    const r = await fetch(`/api/allineamenti/${cassa}/allinea`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ anno, mese }),
    });
    const data = await r.json().catch(() => ({})) as { error?: string; nCessateImplicite?: number };
    if (!r.ok) {
      Weext.toast(data.error ?? 'Errore allineamento', { type: 'danger' });
      return;
    }
    Weext.toast(`Allineamento eseguito (${data.nCessateImplicite ?? 0} cessate)`, { type: 'success' });
    this.caricaStorico();
  }

  // --- Upload Fenice ---

  private async handleUploadFenice(id: number): Promise<void> {
    if (this.feniceStaging) {
      await Weext.alert(
        'Siamo in modalità staging: una volta a regime, questa funzione caricherà il file su Fenice.',
        { title: 'Funzione non attiva' }
      );
      return;
    }
    const result = await this.api.elaborazioni.uploadFenice(id);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
    } else {
      Weext.toast('Caricato su Fenice', { type: 'success' });
    }
    const detailResult = await this.api.elaborazioni.dettaglio(id);
    if (detailResult.ok) {
      const idx = this.elaborazioni.findIndex(e => e.id === id);
      if (idx !== -1) this.elaborazioni[idx] = detailResult.data;
      this.detailCache.set(id, detailResult.data);
      this.renderStorico();
      const contentEl = this.$(`[data-detail-content="${id}"]`);
      const detailRow = this.$(`[data-detail-row="${id}"]`);
      if (contentEl && detailRow && detailRow.style.display !== 'none') {
        templateParseToElement('elaborazioni-dettaglio', contentEl, { elaborazione: detailResult.data, feniceEnabled: this.feniceEnabled });
      }
    }
  }

  // --- Dettaglio inline ---

  private async toggleDettaglio(id: number): Promise<void> {
    const detailRow = this.$(`[data-detail-row="${id}"]`);
    if (!detailRow) return;
    if (detailRow.style.display !== 'none') { detailRow.style.display = 'none'; return; }

    const contentEl = this.$(`[data-detail-content="${id}"]`);
    if (!contentEl) return;

    let elab = this.detailCache.get(id);
    if (!elab) {
      templateParseToElement('shared-loading-state', contentEl, {});
      detailRow.style.display = '';
      const result = await this.api.elaborazioni.dettaglio(id);
      if (!result.ok) {
        templateParseToElement('shared-error-state', contentEl, { message: result.error });
        return;
      }
      elab = result.data;
      this.detailCache.set(id, elab);
    }
    templateParseToElement('elaborazioni-dettaglio', contentEl, { elaborazione: elab, feniceEnabled: this.feniceEnabled });
    detailRow.style.display = '';
  }

  // --- Cleanup ---

  protected override onDisconnected(): void {
    for (const t of this.pollingTimers.values()) clearInterval(t);
    this.pollingTimers.clear();
  }
}
